import {
  ITealiumEvent,
  useTealium,
} from '@grubhubprod/umami-library/dist/analytics/useTealium';
import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useTealiumData } from 'src/common/telemetry/Tealium/useTealiumData';
import { decamelizeRequestBody } from 'src/utilities/http-data-casing';
import { getRouteDetails } from 'src/utilities/route-utils';
import { useCreditCardEnrollmentData } from './useCreditCardEnrollmentData';

export interface IAnalyticsContext {
  sendTealiumPageView: (
    pageViewData: ISelfServiceTealiumPageView | IEnterpriseTealiumPageView
  ) => void;
  sendTealiumEvent: (eventData: TealiumEvent) => void;
}

interface IAnalyticsProviderProps {
  children: ReactNode;
}

export interface ISelfServiceTealiumPageView {
  pageGroup: string;
  pageName: string;
  subGroup?: string;
}

export interface TealiumEvent {
  eventcategory: string;
  eventaction: string;
  eventlabel?: string;
  non_interaction?: string;
}

// tealium GA calls for corp-admin currently have a different field mapping than self-service
export interface IEnterpriseTealiumPageView {
  pagegroup: string;
  pagesubgroup: string;
  corpadmin_creditcardenrollment?: string;
}

enum RouteType {
  ENTERPRISE = 'ENTERPRISE',
  STANDARD = 'STANDARD',
}

const AnalyticsContext = createContext<IAnalyticsContext>(null);

function AnalyticsProvider({ children }: IAnalyticsProviderProps): JSX.Element {
  const {
    sendTealiumPageView: sendTealiumPageViewDefault,
    sendTealiumEvent: sendTealiumEventDefault,
  } = useTealium();
  const tealiumData = useTealiumData();
  const { enrollmentData } = useCreditCardEnrollmentData();
  const { pathname } = useLocation();

  const [routeType, setRouteType] = useState<RouteType>();
  useEffect(() => {
    if (pathname) {
      const { isSelfServicePath, isEnterprisePath } = getRouteDetails(pathname);

      if (isSelfServicePath) {
        setRouteType(RouteType.STANDARD);
      } else if (isEnterprisePath) {
        setRouteType(RouteType.ENTERPRISE);
      }
    }
  }, [pathname]);

  function sendTealiumPageView(
    pageViewData: ISelfServiceTealiumPageView | IEnterpriseTealiumPageView
  ) {
    const viewData = {
      ...tealiumData,
      ...decamelizeRequestBody(enrollmentData),
      ...pageViewData,
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sendTealiumPageViewDefault(viewData as any);
  }

  function sendTealiumEvent(eventData: TealiumEvent) {
    const tealiumEventData = {
      ...tealiumData,
      ...eventData,
    };

    sendTealiumEventDefault(tealiumEventData as unknown as ITealiumEvent);
  }

  // don't render anything until we know the route type
  if (!routeType) {
    return null;
  }

  return (
    <AnalyticsContext.Provider
      value={{
        sendTealiumPageView,
        sendTealiumEvent,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
}

const useAnalytics = (): IAnalyticsContext => {
  const context = useContext(AnalyticsContext);

  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }

  return context;
};

export { AnalyticsProvider, useAnalytics };
